import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { patientFormApi } from '@/services/patientFormApi'
import type { FormConfig, FormField } from '@/types/patientForm'

interface Patient {
  id: number | string
  patient_id: string
  medical_record_number: string
  first_name: string
  last_name: string
  full_name: string
  age: number
  date_of_birth: string
  gender: 'M' | 'F' | 'O' | 'U'
  phone: string
  email: string
  address: string
  emergency_contact: string
  emergency_phone: string
  insurance_number: string
  allergies: string
  medical_history: string
  is_active: boolean
  created_at: string
  updated_at: string
}

interface PatientWithSlides extends Patient {
  slide_count: number
}

const PatientManagement: React.FC = () => {
  const { t } = useTranslation()
  const [patients, setPatients] = useState<PatientWithSlides[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [formConfig, setFormConfig] = useState<FormConfig | null>(null)
  const [formData, setFormData] = useState<Record<string, any>>({})

  const fetchPatients = async () => {
    try {
      setIsLoading(true)
      // Fetch patients and slides
      const [patientsRes, slidesRes] = await Promise.all([
        fetch('/api/v1/pathology/patients/?page_size=1000'),
        fetch('/api/v1/imaging/slides/?page_size=1000')
      ])

      if (!patientsRes.ok) {
        throw new Error('Failed to fetch patients')
      }

      const [patientsData, slidesData] = await Promise.all([
        patientsRes.json(),
        slidesRes.ok ? slidesRes.json() : Promise.resolve({ results: [] })
      ])

      const allPatients = patientsData.results || patientsData
      const slides = (slidesData.results || slidesData) || []

      // Count slides per patient
      const slidesByPatient = new Map<string, number>()
      slides.forEach((slide: any) => {
        if (slide.patient_id) {
          const count = slidesByPatient.get(slide.patient_id.toString()) || 0
          slidesByPatient.set(slide.patient_id.toString(), count + 1)
        }
      })

      // Filter patients without slides (new reception)
      const patientsWithoutSlides = allPatients
        .filter((p: Patient) => !slidesByPatient.has(p.id.toString()))
        .map((p: Patient) => ({
          ...p,
          slide_count: 0
        }))
        .sort((a: Patient, b: Patient) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        )

      setPatients(patientsWithoutSlides)
    } catch (error) {
      console.error('Error fetching patients:', error)
      toast.error('Không thể tải dữ liệu tiếp nhận')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchPatients()
    loadFormTemplate()
  }, [])

  const loadFormTemplate = async () => {
    try {
      const template = await patientFormApi.getDefaultTemplate()
      setFormConfig(template.form_config)
      // Initialize form data with empty values
      const initialData: Record<string, any> = {}
      template.form_config.sections.forEach(section => {
        section.fields.forEach(field => {
          initialData[field.id] = field.type === 'checkbox' ? false : ''
        })
      })
      setFormData(initialData)
    } catch (err) {
      console.error('Error loading form template:', err)
      toast.error('Không thể tải form template')
    }
  }

  const filteredPatients = patients.filter(p =>
    p.patient_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.phone.includes(searchTerm)
  )

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('vi-VN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    })
  }

  const resetForm = () => {
    if (!formConfig) return
    const initialData: Record<string, any> = {}
    formConfig.sections.forEach(section => {
      section.fields.forEach(field => {
        initialData[field.id] = field.type === 'checkbox' ? false : ''
      })
    })
    setFormData(initialData)
  }

  const handleCreatePatient = async () => {
    // Validate required fields
    if (!formConfig) {
      toast.error('Form chưa được tải')
      return
    }

    const missingFields: string[] = []
    formConfig.sections.forEach(section => {
      section.fields.forEach(field => {
        if (field.required && (!formData[field.id] || formData[field.id] === '')) {
          missingFields.push(field.label)
        }
      })
    })

    if (missingFields.length > 0) {
      toast.error(`Vui lòng điền: ${missingFields.join(', ')}`)
      return
    }

    try {
      const response = await fetch('/api/v1/pathology/patients/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          patient_id: '',
          medical_record_number: ''
        })
      })

      if (response.ok) {
        toast.success('Đã thêm bệnh nhân')
        setShowCreateModal(false)
        resetForm()
        fetchPatients()
      } else {
        const errorData = await response.json()
        toast.error(`Lỗi: ${JSON.stringify(errorData)}`)
      }
    } catch (error) {
      console.error('Error creating patient:', error)
      toast.error('Lỗi tạo bệnh nhân')
    }
  }

  const renderField = (field: FormField) => {
    const value = formData[field.id] || ''
    const handleChange = (newValue: any) => {
      setFormData(prev => ({ ...prev, [field.id]: newValue }))
    }

    const inputClasses = "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
    const label = (
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {field.label} {field.required && <span className="text-red-500">*</span>}
      </label>
    )

    switch (field.type) {
      case 'text':
      case 'email':
      case 'phone':
        return (
          <div key={field.id}>
            {label}
            <input
              type={field.type === 'phone' ? 'tel' : field.type}
              value={value}
              onChange={(e) => handleChange(e.target.value)}
              className={inputClasses}
              placeholder={field.placeholder}
              required={field.required}
            />
            {field.help_text && <p className="text-xs text-gray-500 mt-1">{field.help_text}</p>}
          </div>
        )

      case 'textarea':
        return (
          <div key={field.id}>
            {label}
            <textarea
              value={value}
              onChange={(e) => handleChange(e.target.value)}
              className={inputClasses}
              placeholder={field.placeholder}
              rows={2}
              required={field.required}
            />
            {field.help_text && <p className="text-xs text-gray-500 mt-1">{field.help_text}</p>}
          </div>
        )

      case 'date':
        return (
          <div key={field.id}>
            {label}
            <input
              type="date"
              value={value}
              onChange={(e) => handleChange(e.target.value)}
              className={inputClasses}
              required={field.required}
            />
            {field.help_text && <p className="text-xs text-gray-500 mt-1">{field.help_text}</p>}
          </div>
        )

      case 'select':
        return (
          <div key={field.id}>
            {label}
            <select
              value={value}
              onChange={(e) => handleChange(e.target.value)}
              className={inputClasses}
              required={field.required}
            >
              <option value="">-- Chọn --</option>
              {field.options?.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            {field.help_text && <p className="text-xs text-gray-500 mt-1">{field.help_text}</p>}
          </div>
        )

      case 'checkbox':
        return (
          <div key={field.id} className="flex items-start">
            <input
              type="checkbox"
              checked={!!value}
              onChange={(e) => handleChange(e.target.checked)}
              className="mt-1 mr-2"
              required={field.required}
            />
            <div>
              <label className="text-sm font-medium text-gray-700">
                {field.label} {field.required && <span className="text-red-500">*</span>}
              </label>
              {field.help_text && <p className="text-xs text-gray-500">{field.help_text}</p>}
            </div>
          </div>
        )

      default:
        return null
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">{t('common.loading')}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">📋 Tiếp nhận Bệnh nhân</h1>
            <p className="text-gray-600 mt-1">Danh sách bệnh nhân cần upload ảnh slide</p>
          </div>
          <button
            onClick={() => { resetForm(); setShowCreateModal(true) }}
            className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium shadow-md hover:shadow-lg transition-all"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Thêm bệnh nhân mới
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="mb-4">
        <input
          type="text"
          placeholder="Tìm theo mã BN, họ tên, số điện thoại..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      {/* Stats */}
      <div className="mb-4 flex items-center justify-between">
        <div className="text-sm text-gray-600">
          Tổng số: <span className="font-semibold text-gray-900">{filteredPatients.length}</span> bệnh nhân cần upload ảnh
        </div>
        <button
          onClick={fetchPatients}
          className="px-3 py-1 text-sm bg-blue-50 text-blue-600 rounded-md hover:bg-blue-100"
        >
          🔄 Làm mới
        </button>
      </div>

      {/* Table */}
      <div className="bg-white shadow-md rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Mã BN
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Họ và Tên
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Ngày sinh
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Giới tính
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Số điện thoại
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Ngày tiếp nhận
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Thao tác
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {isLoading ? (
              <tr>
                <td colSpan={7} className="px-6 py-4 text-center text-gray-500">
                  Đang tải dữ liệu...
                </td>
              </tr>
            ) : filteredPatients.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-6 py-4 text-center text-gray-500">
                  {searchTerm ? 'Không tìm thấy bệnh nhân' : 'Tất cả bệnh nhân đã có ảnh 🎉'}
                </td>
              </tr>
            ) : (
              filteredPatients.map((patient) => (
                <tr key={patient.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {patient.patient_id}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {patient.full_name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(patient.date_of_birth)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {patient.gender === 'M' ? 'Nam' : patient.gender === 'F' ? 'Nữ' : 'Khác'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {patient.phone}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(patient.created_at)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <Link
                      to={`/imaging/upload?patient_id=${patient.id}`}
                      className="inline-flex items-center px-3 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium"
                    >
                      📤 Upload ảnh
                    </Link>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Create Patient Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-6 border w-11/12 max-w-3xl shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-gray-900">Thêm bệnh nhân mới</h3>
              <button
                onClick={() => setShowCreateModal(false)}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ✕
              </button>
            </div>

            {/* Dynamic Form Sections */}
            {!formConfig ? (
              <div className="text-center py-8 text-gray-500">Đang tải form...</div>
            ) : (
              <div className="space-y-6">
                {formConfig.sections
                  .sort((a, b) => a.order - b.order)
                  .map(section => (
                    <div key={section.id} className="border-b border-gray-200 pb-4 last:border-b-0">
                      <h4 className="text-md font-semibold text-gray-800 mb-3">{section.title}</h4>
                      {section.description && (
                        <p className="text-sm text-gray-600 mb-3">{section.description}</p>
                      )}
                      <div className="grid grid-cols-2 gap-4">
                        {section.fields
                          .sort((a, b) => a.order - b.order)
                          .map(field => (
                            <div key={field.id} className={field.type === 'textarea' ? 'col-span-2' : ''}>
                              {renderField(field)}
                            </div>
                          ))}
                      </div>
                    </div>
                  ))}
              </div>
            )}

            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Hủy
              </button>
              <button
                onClick={handleCreatePatient}
                className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700"
              >
                Thêm bệnh nhân
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default PatientManagement
